import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight, Languages } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useI18n, storeLang } from "@/lib/i18n";
import { HOME_SECTIONS, langPath, stripLang } from "@/lib/routes";

const POLICY_LINKS = [
  { slug: "privacy", labelKey: "nav.privacy", testId: "header-privacy-link" },
  { slug: "cookies", labelKey: "nav.cookies", testId: "header-cookies-link" },
  { slug: "terms", labelKey: "nav.terms", testId: "header-terms-link" },
];

export const Header = () => {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [legalOpen, setLegalOpen] = useState(false);

  const rest = stripLang(location.pathname);
  const isHome = rest === "";
  const otherLang = lang === "ar" ? "en" : "ar";

  const goToSection = (id) => {
    setOpen(false);
    const scroll = () => {
      const el = document.getElementById(id);
      if (el) {
        const top = el.getBoundingClientRect().top + window.pageYOffset - 82;
        window.scrollTo({ top, behavior: "smooth" });
      }
    };
    if (!isHome) {
      navigate(langPath(lang));
      window.setTimeout(scroll, 220);
    } else {
      scroll();
    }
  };

  const switchLanguage = () => {
    storeLang(otherLang);
    setOpen(false);
    // Same path, different prefix -> ScrollToTop intentionally does not fire.
    navigate(langPath(otherLang, rest));
  };

  return (
    <header
      data-no-break="true"
      data-testid="site-header"
      className="sticky top-0 z-50 border-b border-hairline bg-paper/90 backdrop-blur supports-[backdrop-filter]:bg-paper/75"
    >
      <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-2 px-4 py-3 sm:gap-4 sm:px-6 lg:px-8">
        <Link
          to={langPath(lang)}
          data-testid="header-logo-link"
          aria-label={t("common.brand")}
          className="min-w-0 shrink rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber focus-visible:ring-offset-2 focus-visible:ring-offset-paper"
          onClick={() => setOpen(false)}
        >
          <Logo />
        </Link>

        <nav
          aria-label={t("common.menu")}
          className="hidden items-center gap-1 lg:flex"
        >
          {HOME_SECTIONS.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => goToSection(s.id)}
              data-testid={`header-nav-${s.id}`}
              className="whitespace-nowrap rounded-md px-3 py-2 text-[0.82rem] font-medium text-muted-ink transition-colors duration-200 hover:bg-paper2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber"
            >
              {t(s.labelKey)}
            </button>
          ))}
          <div
            className="relative"
            onMouseEnter={() => setLegalOpen(true)}
            onMouseLeave={() => setLegalOpen(false)}
          >
            <button
              type="button"
              onClick={() => setLegalOpen((v) => !v)}
              aria-expanded={legalOpen}
              data-testid="header-legal-toggle"
              className="whitespace-nowrap rounded-md px-3 py-2 text-[0.82rem] font-medium text-muted-ink transition-colors duration-200 hover:bg-paper2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber"
            >
              {t("nav.legal")}
            </button>
            {legalOpen && (
              <div className="absolute right-0 top-full w-64 rounded-xl border border-hairline bg-white p-2 shadow-card">
                {POLICY_LINKS.map((p) => (
                  <Link
                    key={p.slug}
                    to={langPath(lang, p.slug)}
                    data-testid={p.testId}
                    onClick={() => setLegalOpen(false)}
                    className="block rounded-lg px-3 py-2 text-[0.82rem] text-muted-ink transition-colors duration-200 hover:bg-paper2 hover:text-ink"
                  >
                    {t(p.labelKey)}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </nav>

        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
          <button
            type="button"
            onClick={switchLanguage}
            data-testid="header-language-switch"
            aria-label={`${t("lang.label")}: ${t("lang.switchTo")}`}
            title={t("lang.switchTo")}
            className="flex items-center gap-1 rounded-xl border border-hairline px-2 py-2 text-[0.7rem] font-semibold uppercase tracking-[0.06em] text-ink transition-colors duration-200 hover:bg-paper2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber sm:gap-1.5 sm:px-2.5 sm:tracking-[0.1em]"
          >
            <Languages className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
            <span>{t("lang.shortName")}</span>
            <span aria-hidden="true" className="text-hairline">/</span>
            <span className="text-muted-ink/60">{t("lang.switchToShort")}</span>
          </button>

          <button
            type="button"
            onClick={() => goToSection("contacto")}
            data-testid="header-primary-cta"
            className="hidden items-center gap-1.5 whitespace-nowrap rounded-xl bg-ink px-4 py-2.5 text-[0.8rem] font-semibold text-white transition-colors duration-200 hover:bg-ink2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber focus-visible:ring-offset-2 sm:inline-flex"
          >
            {t("header.cta")}
            <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2} />
          </button>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="mobile-nav"
            aria-label={open ? t("common.closeMenu") : t("common.openMenu")}
            data-testid="header-menu-button"
            className="inline-flex items-center justify-center rounded-xl border border-hairline p-2.5 text-ink transition-colors duration-200 hover:bg-paper2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber lg:hidden"
          >
            {open ? (
              <X className="h-5 w-5" strokeWidth={1.75} />
            ) : (
              <Menu className="h-5 w-5" strokeWidth={1.75} />
            )}
          </button>
        </div>
      </div>

      {open && (
        <div
          id="mobile-nav"
          data-testid="mobile-nav"
          className="border-t border-hairline bg-paper lg:hidden"
        >
          <div className="mx-auto w-full max-w-6xl px-4 py-4 sm:px-6">
            <ul className="flex flex-col">
              {HOME_SECTIONS.map((s) => (
                <li key={s.id} className="border-b border-hairline2 last:border-0">
                  <button
                    type="button"
                    onClick={() => goToSection(s.id)}
                    data-testid={`mobile-nav-${s.id}`}
                    className="w-full py-3 text-left text-[0.95rem] font-medium text-ink"
                  >
                    {t(s.labelKey)}
                  </button>
                </li>
              ))}
            </ul>
            <p className="mt-4 font-mono text-[0.6rem] uppercase tracking-[0.16em] text-muted-ink/70">
              {t("nav.legal")}
            </p>
            <ul className="mt-1 flex flex-col">
              {POLICY_LINKS.map((p) => (
                <li key={p.slug} className="border-b border-hairline2 last:border-0">
                  <Link
                    to={langPath(lang, p.slug)}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-${p.testId}`}
                    className="block py-3 text-[0.9rem] text-muted-ink"
                  >
                    {t(p.labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => goToSection("contacto")}
              data-testid="mobile-primary-cta"
              className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-ink px-4 py-3 text-[0.9rem] font-semibold text-white"
            >
              {t("header.cta")}
              <ArrowUpRight className="h-4 w-4" strokeWidth={2} />
            </button>
          </div>
        </div>
      )}
    </header>
  );
};
